// pushes a user interaction event to the window data layer for GA tracking
import forOwn from 'lodash/forOwn';

export const startFormInteractionGaTag = {
  event: 'form-interaction',
  interaction: 'personal-info-start',
  formType: 'configurator payment form',
  engagementType: 'User Requested',
};

export const pushGAInteraction = interactionObject => {
  if (window.dataLayer && window.dataLayer.push) {
    window.dataLayer.push(interactionObject);
  }
};

/**
 * Append a string prefix to every keys in the provided object
 * @param {string} prefix
 * @param {object} obj
 */
export const prefixObject = (prefix, obj) => {
  const objCopy = {};
  forOwn(obj, (value, key) => {
    objCopy[key] = `${prefix} - ${value}`;
  });
  return objCopy;
};

export const wrapGaLabel = (event, interaction, gaLabels) =>
  Object.keys(gaLabels).reduce(
    (prev, label) => ({
      ...prev,
      [label]: {
        event,
        interaction,
        label: gaLabels[label],
      },
    }),
    {}
  );

// GA wrapper to create widget interaction events
export const wrapWidgetInteraction = gaLabels =>
  Object.keys(gaLabels).reduce(
    (prev, label) => ({
      ...prev,
      [label]: {
        event: 'widget-interaction',
        'widget name': gaLabels[label],
      },
    }),
    {}
  );

// GA wrapper to wrap configurator interaction events
export const wrapConfiguratorInteraction = (event, cfgType, gaLabels) =>
  Object.keys(gaLabels).reduce(
    (prev, label) => ({
      ...prev,
      [label]: {
        event,
        interaction: gaLabels[label],
        'cfg-type': cfgType,
      },
    }),
    {}
  );

export const wrapFormInteractionTag = (event, formType, engagementType, gaLabels) =>
  Object.keys(gaLabels).reduce(
    (prev, label) => ({
      ...prev,
      [label]: {
        event,
        formType,
        engagementType,
        interaction: gaLabels[label],
      },
    }),
    {}
  );

export const BUSINESS_UNITS = {
  energy: 'Energy',
  vehicle: 'Vehicle',
};

export const PRODUCT_STATES = {
  preDelivery: 'PreDelivery',
  ownership: 'Ownership',
};

export const PRODUCTS = {
  [BUSINESS_UNITS.vehicle]: {
    modelS: 'Model S',
    modelX: 'Model X',
    model3: 'Model 3',
  },
  [BUSINESS_UNITS.energy]: {
    powerwall: 'Powerwall',
    solarPanels: 'Solar Panels',
    solarRoof: 'Solar Roof',
    homeCharging: 'Home Charging',
  },
};

export class VirtualPageview {
  constructor(businessUnit, productState, product) {
    this.event = {
      event: 'virtualPageview',
    };

    this.allowedBusinessUnits = Object.values(BUSINESS_UNITS);
    this.allowedProductStates = Object.values(PRODUCT_STATES);
    this.allowedProducts = Object.values(PRODUCTS[BUSINESS_UNITS.energy]).concat(
      Object.values(PRODUCTS[BUSINESS_UNITS.vehicle])
    );

    this.updateConfiguration({
      businessUnit,
      productState,
      product,
    });

    // eslint-disable-next-line no-self-assign
    this.updateConfiguration = this.updateConfiguration;
    // eslint-disable-next-line no-self-assign
    this.pushPageview = this.pushPageview;
  }

  pushPageview(pageviewObject) {
    if (pageviewObject.virtualPageUrl) {
      const interactionObject = {
        ...this.event,
        ...pageviewObject,
      };
      // if no virtualPageTitle use the virtualPageUrl for the title as well
      if (!interactionObject.virtualPageTitle) {
        interactionObject.virtualPageTitle = interactionObject.virtualPageUrl;
      }
      pushGAInteraction(interactionObject);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  getValidatedOption(option, allowedOptions, errorLogDescriptor) {
    const trimmedOption = option.trim();
    const isValid = allowedOptions.indexOf(trimmedOption) > -1;
    if (!isValid && errorLogDescriptor) {
      // eslint-disable-next-line no-console
      console.warn(`VirtualPageview: Invalid ${errorLogDescriptor} - ${trimmedOption}`);
    }
    return isValid ? trimmedOption : '';
  }

  updateConfiguration(config) {
    if (config.businessUnit) {
      this.event.MyTeslaBusinessUnit = this.getValidatedOption(
        config.businessUnit,
        this.allowedBusinessUnits,
        'Business Unit'
      );
      if (this.event.MyTeslaBusinessUnit) {
        this.allowedProducts = Object.values(PRODUCTS[this.event.MyTeslaBusinessUnit]);
      }
    }
    if (config.productState) {
      this.event.MyTeslaProductState = this.getValidatedOption(
        config.productState,
        this.allowedProductStates,
        'Product State'
      );
    }
    if (config.product) {
      if (this.event.MyTeslaBusinessUnit === BUSINESS_UNITS.energy) {
        let products = config.product.split(',').map(product => product.trim());
        products = products.filter(product => {
          const validatedOption = this.getValidatedOption(product, this.allowedProducts, 'Product');
          return !!validatedOption;
        });
        this.event.MyTeslaProduct = products.join(',');
      } else {
        this.event.MyTeslaProduct = this.getValidatedOption(
          config.product,
          this.allowedProducts,
          'Product'
        );
      }
    }
  }
}

export const virtualPageview = new VirtualPageview();
